import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { MapData } from '../engine/world/types';
import type { GameAssets } from '../game/assets/GameAssets';
import { WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../game/config';

export type WorldEventType =
  | 'dragon_attack'
  | 'plague'
  | 'drought'
  | 'treasure_found'
  | 'merchant_caravan'
  | 'bandit_attack'
  | 'viking_raid'
  | 'magic_rain';

export interface RuntimeWorldEvent {
  readonly type: WorldEventType;
  readonly title: string;
  readonly command: string;
}

const EVENT_COLORS: Record<WorldEventType, number> = {
  dragon_attack: 0xe14a27,
  plague: 0x74a83b,
  drought: 0xc98a3a,
  treasure_found: 0xffcf48,
  merchant_caravan: 0xd59a55,
  bandit_attack: 0x8f2d25,
  viking_raid: 0xb92f2f,
  magic_rain: 0x7be4ff,
};

export class WorldEventEffects {
  readonly root = new Container();
  readonly banner = new Container();
  private readonly atmosphere = new Graphics();
  private readonly actors = new Graphics();
  private readonly dragonView: Container;
  private readonly caravanView: Sprite;
  private readonly treasureView: Sprite;
  private readonly bannerBackground = new Graphics();
  private readonly vikingViews: readonly Container[];
  private readonly banditViews: readonly Container[];
  private roadRoute: readonly { x: number; y: number }[] = [];
  private roadRouteSignature = '';
  private readonly bannerText = new Text({
    text: '',
    style: {
      fill: '#fff2c4',
      fontFamily: 'Georgia, serif',
      fontSize: 16,
      fontWeight: 'bold',
      stroke: { color: '#160b04', width: 3 },
      dropShadow: { color: '#000000', alpha: 0.75, blur: 3, distance: 1 },
    },
  });
  private current: RuntimeWorldEvent | null = null;
  private elapsed = 0;

  constructor(worldLayer: Container, uiLayer: Container, entityLayer: Container, assets: GameAssets) {
    this.root.label = 'Weltevent-Grafikeffekte';
    this.root.sortableChildren = true;
    this.dragonView = this.createVoxelDragonView();
    this.dragonView.label = 'Voxel-Drache';
    this.dragonView.visible = false;
    this.caravanView = new Sprite(assets.reference['events/merchant_caravan_pixel_v3']);
    this.caravanView.label = 'Händlerkarawane';
    this.caravanView.anchor.set(0.5, 1);
    this.caravanView.visible = false;
    this.treasureView = new Sprite(assets.reference['events/treasure_found_pixel_v1']);
    this.treasureView.label = 'Pixelart-Schatz';
    this.treasureView.anchor.set(0.5, 1);
    this.treasureView.visible = false;
    this.root.addChild(this.atmosphere, this.actors, this.dragonView, this.treasureView);
    this.banner.label = 'Weltevent-Anzeige';
    this.banner.addChild(this.bannerBackground, this.bannerText);
    this.banner.position.set(700, 60);
    this.bannerText.anchor.set(0.5);
    this.bannerText.position.set(260, 20);
    worldLayer.addChild(this.root);
    uiLayer.addChild(this.banner);
    this.vikingViews = Array.from({ length: 1 }, (_, index) => {
      const view = new Container();
      const sprite = new Sprite(assets.reference['events/viking_raid_pixel_v1']);
      sprite.anchor.set(0.5, 1);
      view.label = `Wikingergruppe ${index + 1}`;
      view.addChild(sprite);
      view.visible = false;
      entityLayer.addChild(view);
      return view;
    });
    this.banditViews = Array.from({ length: 3 }, (_, index) => {
      const view = this.createBanditView(index);
      view.visible = false;
      entityLayer.addChild(view);
      return view;
    });
    entityLayer.addChild(this.caravanView);
    this.root.visible = false;
    this.banner.visible = false;
  }

  setEvent(event: RuntimeWorldEvent | null): void {
    if (!event && !this.current) return;
    if (event && this.current && event.type === this.current.type && event.title === this.current.title) return;
    this.current = event;
    this.elapsed = 0;
    this.root.visible = Boolean(event);
    this.banner.visible = Boolean(event);
    this.caravanView.visible = event?.type === 'merchant_caravan';
    this.dragonView.visible = event?.type === 'dragon_attack';
    this.treasureView.visible = event?.type === 'treasure_found';
    this.setVikingVisibility(event?.type === 'viking_raid');
    this.setBanditVisibility(event?.type === 'bandit_attack');
    this.drawBanner();
  }

  update(deltaMS: number, map?: MapData): void {
    if (!this.current) return;
    this.elapsed += deltaMS / 1000;
    this.atmosphere.clear();
    this.actors.clear();
    this.caravanView.visible = false;
    this.dragonView.visible = false;
    this.treasureView.visible = false;

    switch (this.current.type) {
      case 'dragon_attack': this.drawDragon(map); break;
      case 'plague': this.drawPlague(map); break;
      case 'drought': this.drawDrought(map); break;
      case 'treasure_found': this.drawTreasure(map); break;
      case 'merchant_caravan': this.drawCaravan(map); break;
      case 'bandit_attack': this.drawBandits(map); break;
      case 'viking_raid': this.drawVikings(map); break;
      case 'magic_rain': this.drawMagicRain(map); break;
    }
  }

  private drawBanner(): void {
    this.bannerBackground.clear();
    if (!this.current) return;
    const color = EVENT_COLORS[this.current.type];
    this.bannerBackground
      .roundRect(0, 0, 520, 40, 9)
      .fill({ color: 0x120d08, alpha: 0.88 })
      .stroke({ color, width: 2, alpha: 0.95 });
    this.bannerBackground
      .roundRect(6, 5, 508, 30, 6)
      .stroke({ color: 0xffe7a0, width: 1, alpha: 0.35 });
    this.bannerText.text = `WELTEVENT: ${this.current.title}${this.current.command ? `  ·  ${this.current.command}` : ''}`;
  }

  private drawDragon(map?: MapData): void {
    const t = this.elapsed;
    const bounds = this.mapBounds(map);
    const patrolWidth = Math.max(480, bounds.width * 0.54);
    const patrol = (t * 0.075) % 2;
    const movingEast = patrol <= 1;
    const progress = movingEast ? patrol : 2 - patrol;
    const facing = movingEast ? 1 : -1;
    const x = bounds.left + bounds.width * 0.2 + progress * patrolWidth;
    const y = bounds.top + bounds.height * 0.26 + Math.sin(t * 1.7) * 18;
    const targets = this.tileCenters(map, ['road', 'field', 'workyard']);
    const target = targets.length
      ? targets[Math.floor(t / 5) % targets.length]
      : { x: bounds.left + bounds.width * 0.55, y: bounds.top + bounds.height * 0.58 };
    this.atmosphere.rect(bounds.left, bounds.top, bounds.width, bounds.height).fill({ color: 0x49160e, alpha: 0.055 });
    for (let i = 0; i < 7; i += 1) {
      const life = (t * 1.8 + i * 0.37) % 1;
      const fx = target.x + Math.sin(i * 7.1) * 34 + life * 18;
      const fy = target.y + 10 - life * 65;
      const size = 3 + Math.floor((1 - life) * 2);
      this.actors
        .rect(Math.round(fx), Math.round(fy), size * 2, size * 2)
        .fill({ color: i % 2 ? 0xffb02e : 0xef3d21, alpha: 0.76 - life * 0.5 });
    }

    const wingPulse = 1 + Math.sin(t * 7.5) * 0.035;
    this.dragonView.visible = true;
    this.dragonView.position.set(x, y + 46);
    this.dragonView.scale.set(facing * 0.82, 0.82 * wingPulse);
    this.dragonView.zIndex = Math.floor(y + 120);
  }

  /**
   * Builds the dragon from individually shaded cuboids. Keeping the event
   * actor procedural makes it match the voxel citizens at every zoom level
   * and avoids falling back to the former flat pixel-art sprite.
   */
  private createVoxelDragonView(): Container {
    const view = new Container();
    const shadow = new Graphics();
    const dragon = new Graphics();
    shadow.ellipse(-3, 2, 108, 20).fill({ color: 0x070504, alpha: 0.3 });

    const red = '#a9362c';
    const redDark = '#70251f';
    const redLight = '#d75a3d';
    const wing = '#8c302b';
    const horn = '#e4c47d';

    // Tail, legs and claws establish a readable three-dimensional silhouette.
    this.drawVoxelCuboid(dragon, -61, -28, 22, 10, 5, redDark);
    this.drawVoxelCuboid(dragon, -78, -22, 20, 8, 4, redDark);
    this.drawVoxelCuboid(dragon, -88, -17, 14, 6, 3, redLight);
    this.drawVoxelCuboid(dragon, -30, -16, 13, 18, 4, redDark);
    this.drawVoxelCuboid(dragon, 17, -16, 13, 18, 4, redDark);
    this.drawVoxelCuboid(dragon, -35, -2, 20, 7, 3, '#3b251b');
    this.drawVoxelCuboid(dragon, 14, -2, 20, 7, 3, '#3b251b');

    // Layered block wings, body, neck and head use the same light/top/side
    // treatment as characters and bandits.
    this.drawVoxelCuboid(dragon, -42, -68, 25, 13, 6, wing);
    this.drawVoxelCuboid(dragon, -55, -79, 22, 11, 5, redLight);
    this.drawVoxelCuboid(dragon, -63, -87, 17, 9, 4, wing);
    this.drawVoxelCuboid(dragon, 16, -68, 25, 13, 6, wing);
    this.drawVoxelCuboid(dragon, 33, -79, 22, 11, 5, redLight);
    this.drawVoxelCuboid(dragon, 47, -87, 17, 9, 4, wing);
    this.drawVoxelCuboid(dragon, -39, -47, 72, 31, 7, red);
    this.drawVoxelCuboid(dragon, 22, -62, 22, 28, 6, redLight);
    this.drawVoxelCuboid(dragon, 36, -76, 36, 24, 6, red);

    // Snout, horns, eyes and spine remain legible in a 1080p OBS scene.
    this.drawVoxelCuboid(dragon, 62, -67, 22, 14, 4, redLight);
    this.drawVoxelCuboid(dragon, 43, -82, 7, 12, 3, horn);
    this.drawVoxelCuboid(dragon, 62, -83, 7, 11, 3, horn);
    dragon.rect(62, -69, 5, 5).fill('#f6da64');
    dragon.rect(64, -68, 2, 3).fill('#24150d');
    dragon.rect(80, -61, 4, 3).fill('#2a1810');
    for (let index = 0; index < 5; index += 1) {
      this.drawVoxelCuboid(dragon, -28 + index * 13, -54 - (index % 2) * 3, 7, 9, 3, horn);
    }

    view.addChild(shadow, dragon);
    return view;
  }

  private drawPlague(map?: MapData): void {
    const bounds = this.mapBounds(map);
    const affected = this.spreadPoints(this.tileCenters(map, ['road', 'workyard']), 9);
    this.atmosphere.rect(bounds.left, bounds.top, bounds.width, bounds.height).fill({ color: 0x183719, alpha: 0.09 });
    for (let i = 0; i < affected.length; i += 1) {
      const point = affected[i];
      const pulse = 1 + Math.sin(this.elapsed * 2 + i) * 0.2;
      const x = point.x + Math.sin(this.elapsed + i * 2.4) * 12;
      const y = point.y - 8 + Math.cos(this.elapsed * 1.3 + i) * 8;
      this.atmosphere.rect(x - 22, y - 14, 44, 28).fill({ color: i % 2 ? 0x769c43 : 0x395e32, alpha: 0.08 });
      this.actors.rect(Math.round(x - 4 * pulse), Math.round(y - 4 * pulse), 8 * pulse, 8 * pulse).fill({ color: 0x7fa947, alpha: 0.66 });
      this.actors.rect(Math.round(x - 11), Math.round(y - 6), 5, 5).fill({ color: 0xb2dc64, alpha: 0.72 });
      this.actors.rect(Math.round(x + 6), Math.round(y + 2), 6, 6).fill({ color: 0x6b913c, alpha: 0.66 });
    }
  }

  private drawDrought(map?: MapData): void {
    const bounds = this.mapBounds(map);
    const dryTiles = this.spreadPoints(this.tileCenters(map, ['field', 'grass', 'workyard']), 18);
    this.atmosphere.rect(bounds.left, bounds.top, bounds.width, bounds.height).fill({ color: 0xb66b1d, alpha: 0.09 });
    for (let i = 0; i < dryTiles.length; i += 1) {
      const { x, y } = dryTiles[i];
      const length = 10 + (i % 4) * 4;
      const crack = [[-length, 0], [-5, 4], [2, -4], [length, 2]] as const;
      for (let pointIndex = 0; pointIndex < crack.length - 1; pointIndex += 1) {
        const from = crack[pointIndex];
        const to = crack[pointIndex + 1];
        this.actors
          .moveTo(Math.round(x + from[0]), Math.round(y + from[1]))
          .lineTo(Math.round(x + to[0]), Math.round(y + to[1]))
          .stroke({ color: 0x6b3b19, width: 3, alpha: 0.58 });
      }
      const dustX = x + Math.sin(this.elapsed * 1.2 + i) * 19;
      const dustY = y - 8 - ((this.elapsed * 13 + i * 9) % 28);
      this.actors.rect(Math.round(dustX), Math.round(dustY), 4 + (i % 2) * 2, 4 + (i % 2) * 2).fill({ color: 0xe4b66b, alpha: 0.42 });
    }
  }

  private drawTreasure(map?: MapData): void {
    const pulse = 1 + Math.sin(this.elapsed * 3) * 0.08;
    this.ensureRoadRoute(map);
    const maximum = Math.max(1, this.roadRoute.length - 1);
    const routeDistance = maximum * 0.58;
    const roadPoint = this.positionOnRoad(routeDistance);
    const roadNext = this.positionOnRoad(Math.min(maximum, routeDistance + 0.08));
    const segmentLength = Math.hypot(roadNext.x - roadPoint.x, roadNext.y - roadPoint.y) || 1;
    const x = roadPoint.x - ((roadNext.y - roadPoint.y) / segmentLength) * 42;
    const y = roadPoint.y + ((roadNext.x - roadPoint.x) / segmentLength) * 42;
    this.actors.rect(x - 38 * pulse, y - 25 * pulse, 76 * pulse, 50 * pulse).fill({ color: 0xffd54f, alpha: 0.055 });
    this.treasureView.visible = true;
    this.treasureView.position.set(x, y + 18);
    this.treasureView.scale.set(0.76 * pulse);
    this.treasureView.zIndex = Math.floor(y + 20);
    for (let i = 0; i < 8; i += 1) {
      const angle = i * Math.PI / 4 + this.elapsed * 0.35;
      const radius = 34 + ((this.elapsed * 12 + i * 7) % 22);
      const sx = x + Math.cos(angle) * radius;
      const sy = y + Math.sin(angle) * radius * 0.55;
      this.actors.rect(Math.round(sx - 2), Math.round(sy - 2), 5, 5).fill({ color: 0xffef91, alpha: 0.82 });
    }
  }

  private drawCaravan(map?: MapData): void {
    this.ensureRoadRoute(map);
    const maximum = Math.max(1, this.roadRoute.length - 1);
    const motion = this.positionInPatrol(this.elapsed * 0.72, maximum);
    const routeDistance = motion.distance;
    const position = this.positionOnRoad(routeDistance);
    const next = this.positionOnRoad(
      Math.max(0, Math.min(maximum, routeDistance + (motion.movingForward ? 0.08 : -0.08))),
    );
    const direction = next.x < position.x ? -1 : 1;
    const bob = Math.round(Math.sin(this.elapsed * 8) * 1.4);
    const x = position.x;
    const y = position.y + bob;
    this.actors.ellipse(x, y + 7, 44, 9).fill({ color: 0x050505, alpha: 0.25 });
    this.caravanView.visible = true;
    this.caravanView.position.set(x, y + 22);
    this.caravanView.scale.set(direction * 0.68, 0.68);
    // Event actors traverse the bridge as visible gameplay units. The bridge
    // sprite has a raised deck z-offset, so the caravan needs enough depth
    // bias to remain readable instead of disappearing behind the full deck.
    this.caravanView.zIndex = Math.floor(y + 42);
  }

  private drawVikings(map?: MapData): void {
    this.atmosphere.rect(0, 0, 1920, 1080).fill({ color: 0x4b0f0f, alpha: 0.08 });
    this.ensureRoadRoute(map);
    this.setVikingVisibility(true);

    const maximum = Math.max(1, this.roadRoute.length - 1);
    const greatestTrailingDistance = Math.floor((this.vikingViews.length - 1) / 2) * 0.68 + 0.16;
    const patrolMargin = Math.min(greatestTrailingDistance, maximum * 0.22);
    const motion = this.positionInPatrol(this.elapsed * 0.95 + 0.9, maximum, patrolMargin);
    const movingForward = motion.movingForward;
    const leaderDistance = motion.distance;

    for (let i = 0; i < this.vikingViews.length; i += 1) {
      const formationRow = Math.floor(i / 2);
      const formationSide = i % 2 === 0 ? -1 : 1;
      const trailingDistance = formationRow * 0.68 + (i % 2) * 0.16;
      const routeDistance = Math.max(
        0,
        Math.min(maximum, leaderDistance + (movingForward ? -trailingDistance : trailingDistance)),
      );
      const vikingView = this.vikingViews[i];
      vikingView.visible = true;
      const position = this.positionOnRoad(routeDistance);
      const next = this.positionOnRoad(
        Math.max(0, Math.min(maximum, routeDistance + (movingForward ? 0.08 : -0.08))),
      );
      const length = Math.hypot(next.x - position.x, next.y - position.y) || 1;
      const sideX = -(next.y - position.y) / length;
      const sideY = (next.x - position.x) / length;
      const x = position.x + sideX * formationSide * 11;
      const y = position.y + sideY * formationSide * 7;
      this.drawVikingFigure(vikingView, i, x, y, next.x < position.x, this.elapsed * 9 + i * 0.8);
    }
  }

  private setVikingVisibility(visible: boolean): void {
    for (const view of this.vikingViews) view.visible = visible;
  }

  private drawBandits(map?: MapData): void {
    const bounds = this.mapBounds(map);
    this.atmosphere.rect(bounds.left, bounds.top, bounds.width, bounds.height).fill({ color: 0x32100d, alpha: 0.07 });
    this.ensureRoadRoute(map);
    this.setBanditVisibility(true);

    const maximum = Math.max(1, this.roadRoute.length - 1);
    const motion = this.positionInPatrol(this.elapsed * 0.78 + 0.55, maximum, Math.min(0.9, maximum * 0.18));
    for (let index = 0; index < this.banditViews.length; index += 1) {
      const trailing = index * 0.34;
      const distance = Math.max(0, Math.min(maximum, motion.distance + (motion.movingForward ? -trailing : trailing)));
      const position = this.positionOnRoad(distance);
      const next = this.positionOnRoad(Math.max(0, Math.min(maximum, distance + (motion.movingForward ? 0.08 : -0.08))));
      const length = Math.hypot(next.x - position.x, next.y - position.y) || 1;
      const sideX = -(next.y - position.y) / length;
      const sideY = (next.x - position.x) / length;
      const laneOffset = index === 1 ? 7 : index === 2 ? -7 : 0;
      const view = this.banditViews[index];
      view.visible = true;
      view.position.set(position.x + sideX * laneOffset, position.y + sideY * laneOffset + Math.round(Math.sin(this.elapsed * 8 + index) * 1.2));
      view.scale.set(next.x < position.x ? -0.78 : 0.78, 0.78);
      view.zIndex = Math.floor(position.y + sideY * laneOffset + 5);
    }
  }

  private setBanditVisibility(visible: boolean): void {
    for (const view of this.banditViews) view.visible = visible;
  }

  private createBanditView(index: number): Container {
    const view = new Container();
    const graphics = new Graphics();
    const tunic = ['#71423a', '#4d5260', '#674533'][index % 3];
    view.label = `Voxel-Bandit ${index + 1}`;

    this.drawVoxelCuboid(graphics, -11, -24, 9, 22, 3, '#2b2628');
    this.drawVoxelCuboid(graphics, 3, -24, 9, 22, 3, '#30292a');
    this.drawVoxelCuboid(graphics, -13, -7, 11, 7, 3, '#171417');
    this.drawVoxelCuboid(graphics, 3, -7, 11, 7, 3, '#171417');
    this.drawVoxelCuboid(graphics, -17, -52, 34, 30, 4, tunic);
    this.drawVoxelCuboid(graphics, -23, -49, 7, 25, 3, '#3a2b2b');
    this.drawVoxelCuboid(graphics, 17, -49, 7, 25, 3, '#3a2b2b');
    this.drawVoxelCuboid(graphics, -13, -75, 27, 24, 4, '#e7bd91');
    this.drawVoxelCuboid(graphics, -17, -80, 35, 13, 5, '#29252b');
    graphics.rect(-14, -68, 30, 9).fill('#3b3033');
    graphics.rect(-7, -66, 4, 3).fill('#f1d16c');
    graphics.rect(6, -66, 4, 3).fill('#f1d16c');
    this.drawVoxelCuboid(graphics, 21, -43, 4, 38, 2, '#6e4b2d');
    this.drawVoxelCuboid(graphics, 18, -47, 11, 6, 2, '#aeb7c1');
    view.addChild(graphics);
    return view;
  }

  private drawVoxelCuboid(graphics: Graphics, x: number, y: number, width: number, height: number, depth: number, color: string): void {
    const outline = { color: 0x211719, width: 0.7, alpha: 0.82 };
    graphics.rect(x, y, width, height).fill(color).stroke(outline);
    graphics.poly([x, y, x + depth, y - depth, x + width + depth, y - depth, x + width, y]).fill(this.shadeColor(color, 0.28)).stroke(outline);
    graphics.poly([x + width, y, x + width + depth, y - depth, x + width + depth, y + height - depth, x + width, y + height]).fill(this.shadeColor(color, -0.25)).stroke(outline);
  }

  private shadeColor(value: string, factor: number): number {
    const normalized = value.slice(1);
    const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16));
    const adjusted = channels.map((channel) => Math.max(0, Math.min(255, Math.round(factor >= 0 ? channel + (255 - channel) * factor : channel * (1 + factor)))));
    return (adjusted[0] << 16) | (adjusted[1] << 8) | adjusted[2];
  }

  private ensureRoadRoute(map?: MapData): void {
    const signature = map ? `${map.width}x${map.height}:${map.tiles.length}` : 'fallback';
    if (this.roadRoute.length > 1 && this.roadRouteSignature === signature) return;
    this.roadRouteSignature = signature;

    // Spielfiguren behandeln Brücken als begehbare Straßenfelder. Event-Akteure
    // verwenden exakt dieselbe Regel, damit sie niemals neben der Brücke laufen
    // oder am Flussende verschwinden.
    const roadTiles = map?.tiles.filter((tile) =>
      tile.type === 'road' || tile.variant === 'bridge_h' || tile.variant === 'bridge_v'
    ) ?? [];
    if (roadTiles.length < 2) {
      this.roadRoute = [
        { x: 1800, y: 480 },
        { x: 1510, y: 480 },
        { x: 1240, y: 480 },
        { x: 980, y: 480 },
        { x: 720, y: 480 },
        { x: 520, y: 370 },
      ];
      return;
    }

    const nodes = new Map(roadTiles.map((tile) => [`${tile.x},${tile.y}`, tile]));
    const start = roadTiles.reduce((best, tile) => tile.x > best.x ? tile : best, roadTiles[0]);
    const queue = [start];
    const parents = new Map<string, string | null>([[`${start.x},${start.y}`, null]]);
    let farthestKey = `${start.x},${start.y}`;

    for (let index = 0; index < queue.length; index += 1) {
      const tile = queue[index];
      farthestKey = `${tile.x},${tile.y}`;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const key = `${tile.x + dx},${tile.y + dy}`;
        const neighbor = nodes.get(key);
        if (!neighbor || parents.has(key)) continue;
        parents.set(key, `${tile.x},${tile.y}`);
        queue.push(neighbor);
      }
    }

    const routeKeys: string[] = [];
    let cursor: string | null = farthestKey;
    while (cursor) {
      routeKeys.push(cursor);
      cursor = parents.get(cursor) ?? null;
    }

    this.roadRoute = routeKeys.reverse().map((key) => {
      const tile = nodes.get(key)!;
      return {
        x: tile.x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2,
        y: tile.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT / 2,
      };
    });
  }

  private positionOnRoad(distance: number): { x: number; y: number } {
    const lower = Math.max(0, Math.min(this.roadRoute.length - 1, Math.floor(distance)));
    const upper = Math.min(this.roadRoute.length - 1, lower + 1);
    const blend = distance - lower;
    const from = this.roadRoute[lower];
    const to = this.roadRoute[upper];
    return {
      x: from.x + (to.x - from.x) * blend,
      y: from.y + (to.y - from.y) * blend,
    };
  }

  private positionInPatrol(
    travelledDistance: number,
    maximum: number,
    margin = 0,
  ): { distance: number; movingForward: boolean } {
    const safeMargin = Math.max(0, Math.min(margin, maximum / 2));
    const span = Math.max(0.001, maximum - safeMargin * 2);
    const cyclePosition = ((travelledDistance % (span * 2)) + span * 2) % (span * 2);
    const movingForward = cyclePosition <= span;
    return {
      distance: safeMargin + (movingForward ? cyclePosition : span * 2 - cyclePosition),
      movingForward,
    };
  }

  private drawVikingFigure(
    view: Container,
    _index: number,
    x: number,
    y: number,
    facesWest: boolean,
    phase: number,
  ): void {
    const sprite = view.children[0] as Sprite;
    const step = Math.round(Math.sin(phase) * 1.5);
    view.position.set(x, y);
    sprite.y = step;
    view.scale.set(facesWest ? -0.72 : 0.72, 0.72);
    view.zIndex = Math.floor(y + 4);
  }

  private drawMagicRain(map?: MapData): void {
    const bounds = this.mapBounds(map);
    const blessedTiles = this.spreadPoints(this.tileCenters(map, ['field', 'forest', 'grass']), 16);
    this.atmosphere.rect(bounds.left, bounds.top, bounds.width, bounds.height).fill({ color: 0x18345f, alpha: 0.07 });
    for (let i = 0; i < blessedTiles.length; i += 1) {
      const point = blessedTiles[i];
      const fall = (this.elapsed * (84 + (i % 4) * 10) + i * 31) % 74;
      const x = point.x + ((i * 17) % 36) - 18;
      const y = point.y - 58 + fall;
      this.actors
        .rect(Math.round(x), Math.round(y), 4, 12)
        .fill({ color: i % 4 ? 0x74dfff : 0xd98cff, alpha: 0.65 });
      const phase = this.elapsed * 2 + i * 1.7;
      const size = 2 + (Math.sin(phase * 1.6) + 1) * 2;
      this.actors
        .rect(Math.round(point.x - size / 2), Math.round(point.y + 9 - size / 2), Math.max(3, size), Math.max(3, size))
        .fill({ color: 0xf4dcff, alpha: 0.78 });
    }
  }

  private tileCenters(map: MapData | undefined, types: readonly string[]): { x: number; y: number }[] {
    if (!map) return [];
    return map.tiles
      .filter((tile) => types.includes(tile.type))
      .map((tile) => ({
        x: tile.x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2,
        y: tile.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT / 2,
      }));
  }

  private spreadPoints(points: readonly { x: number; y: number }[], count: number): { x: number; y: number }[] {
    if (points.length <= count) return [...points];
    const selected: { x: number; y: number }[] = [];
    const step = points.length / count;
    for (let index = 0; index < count; index += 1) {
      selected.push(points[Math.floor(index * step)]);
    }
    return selected;
  }

  private mapBounds(map?: MapData): { left: number; top: number; width: number; height: number } {
    if (!map) return { left: 0, top: 0, width: 1920, height: 1080 };
    return {
      left: 0,
      top: 0,
      width: map.width * WORLD_TILE_WIDTH,
      height: map.height * WORLD_TILE_HEIGHT,
    };
  }
}

export function normalizeWorldEvent(value: unknown): RuntimeWorldEvent | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const rawType = String(row.type || row.event_type || row.monster || '').toLowerCase();
  const aliases: Record<string, WorldEventType> = {
    dragon: 'dragon_attack',
    dragon_attack: 'dragon_attack',
    plague: 'plague',
    drought: 'drought',
    treasure: 'treasure_found',
    treasure_found: 'treasure_found',
    caravan: 'merchant_caravan',
    merchant_caravan: 'merchant_caravan',
    bandit: 'bandit_attack',
    bandit_attack: 'bandit_attack',
    raider: 'viking_raid',
    viking_raid: 'viking_raid',
    magic_rain: 'magic_rain',
  };
  const type = aliases[rawType];
  if (!type) return null;
  return {
    type,
    title: String(row.title || row.name || rawType).slice(0, 100),
    command: String(row.command || '').slice(0, 40),
  };
}
